/**
 * The person record's rail — the ONE definition of it (#462 §5, "the step list drawn twice").
 *
 * WHY THIS EXISTS. The same seven numbered steps were written out twice: once in
 * `pages/PersonPage.tsx`, once in `features/offers/OfferRecordFrame.tsx`, so the offer routes and
 * the record itself each carried their own copy. They had already drifted — the offer routes still
 * drew **Orientation and start** as unlinked, with a comment saying it had no screen, which stopped
 * being true when #346 gave step 7 its content. Two clicks apart, the same person showed two
 * different rails, which is the complaint #332 was about.
 *
 * So the order and the labels live here, and each surface renders them its own way: the record
 * turns them into sections, the offer frame into links. Neither can renumber or rename a step
 * without the other following.
 */

export interface CandidateStepSpec {
  /** Matches `?section=` on the person record, which is how that page selects a section. */
  readonly id: string;
  readonly label: string;
  /**
   * False while the app has no screen for this step.
   *
   * The step is still DRAWN — the frames draw seven, and a rail that skipped one would misstate
   * how many there are. It is drawn unlinked, rather than pointed somewhere it does not belong.
   *
   * D13 (#462 §5) asks whether an unbuilt step should be hidden and the visible ones renumbered
   * 1 to 6 instead. If that is decided, this flag is the single place it changes.
   */
  readonly built: boolean;
}

/** The seven numbered Candidate steps the approved frames name, in their order. */
export const candidateStepRail: readonly CandidateStepSpec[] = [
  { id: 'overview', label: 'Overview', built: true },
  { id: 'hiring', label: 'Offer and stage', built: true },
  // Built by #472: frame `278:6492`, and the link control moved here out of step 4.
  { id: 'portal', label: 'Portal', built: true },
  { id: 'documents', label: 'Documents & checklist', built: true },
  // Built by #487: frame `277:6308`, the one candidate's side of the review queue.
  { id: 'review', label: 'Review', built: true },
  { id: 'profile', label: 'Profile', built: true },
  // Built by #346. The offer routes said otherwise until this list replaced their copy.
  { id: 'orientation', label: 'Orientation and start', built: true },
];

/** The links under the rail's divider. All three are sections of the record, not numbered steps. */
export const recordLinkRail: readonly { readonly id: string; readonly label: string }[] = [
  { id: 'activity', label: 'Follow-ups & messages' },
  { id: 'history', label: 'History' },
  { id: 'settings', label: 'Agency settings' },
];

/**
 * The label for one rail id — the ONE place either surface reads it from.
 *
 * #467 made the order and the built/unbuilt flags single-source and left the LABELS duplicated:
 * the person record declared its own copy on each section, so `Profile` could be renamed here and
 * the offer routes would show the new name while the record showed the old one, with the whole
 * suite still green. Julio found that by applying the rename. These two functions are what the
 * record calls instead of writing the string again.
 *
 * Throws rather than falling back to the id. A missing label is a wiring mistake, and a rail that
 * silently reads `profile` where it should read `Profile` is worse than one that fails to build.
 */
export function stepLabel(id: string): string {
  const step = candidateStepRail.find((entry) => entry.id === id);
  if (step === undefined) throw new Error(`no candidate step named ${id}`);
  return step.label;
}

export function recordLinkLabel(id: string): string {
  const link = recordLinkRail.find((entry) => entry.id === id);
  if (link === undefined) throw new Error(`no record link named ${id}`);
  return link.label;
}
